import React, { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import {
  listEventLists,
  listSessionLists,
  listVideoData,
} from '../../graphql/queries';
import { getVideoChips } from '../../utils/chipColors';
import CarSessionCard from '../session-viewer-enhanced/CarSessionCard';

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '24px',
    color: '#1976d2',
  },
  filterCard: {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    minWidth: '180px',
  },
  clearButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  viewModeButton: {
    padding: '8px 16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  viewModeActive: {
    background: '#1976d2',
    color: 'white',
    borderColor: '#1976d2',
  },
  eventCard: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  eventHeader: {
    background: '#f8f9fa',
    padding: '16px',
    borderBottom: '1px solid #ddd',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    margin: 0,
  },
  eventSubtitle: {
    color: '#666',
    fontSize: '0.875rem',
    margin: '4px 0 0 0',
  },
  sessionSection: {
    padding: '16px',
    borderBottom: '1px solid #eee',
  },
  sessionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  sessionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1976d2',
    margin: 0,
  },
  sessionTime: {
    color: '#666',
    fontSize: '0.875rem',
  },
  carGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-start',
  },
  carCard: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden',
    width: '320px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  carCardSelected: {
    border: '2px solid #1976d2',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
  },
  carHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '12px 16px 0 16px',
  },
  carNumber: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  playButton: {
    background: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    transition: 'background 0.2s',
    boxShadow: '0 2px 6px rgba(25, 118, 210, 0.10)',
  },
  playButtonHover: {
    background: '#1251a3',
  },
  carDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0 16px 16px 16px',
  },
  carInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.875rem',
  },
  chipContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '12px',
    marginBottom: '4px',
    paddingLeft: '2px',
  },
  mobileFilters: {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  mobileFilterHeader: {
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #ddd',
  },
  mobileFilterContent: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  error: {
    background: '#ffebee',
    color: '#d32f2f',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
};

// Desktop view mode selector - moved outside to prevent recreation on each render
const ViewModeSelector = ({
  viewMode,
  filters,
  handleViewModeChange,
  setFilters,
  clearFilters,
  styles,
}) => (
  <div style={styles.filterCard}>
    <div style={styles.filterRow}>
      <strong>View by:</strong>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          style={{
            ...styles.viewModeButton,
            ...(viewMode === 'event' ? styles.viewModeActive : {}),
          }}
          onClick={() => handleViewModeChange('event')}
        >
          Event
        </button>
        <button
          style={{
            ...styles.viewModeButton,
            ...(viewMode === 'track' ? styles.viewModeActive : {}),
          }}
          onClick={() => handleViewModeChange('track')}
        >
          Track
        </button>
      </div>
      <input
        key="desktop-track-filter"
        style={styles.filterInput}
        placeholder={viewMode === 'event' ? 'Track/Venue' : 'Track Name'}
        value={filters.track}
        onChange={(e) => setFilters({ ...filters, track: e.target.value })}
      />
      <input
        key="desktop-driver-filter"
        style={styles.filterInput}
        placeholder="Driver Name"
        value={filters.driver}
        onChange={(e) => setFilters({ ...filters, driver: e.target.value })}
      />
      <button style={styles.clearButton} onClick={clearFilters}>
        Clear
      </button>
    </div>
  </div>
);

// Mobile view mode selector - moved outside to prevent recreation on each render
const MobileViewModeSelector = ({
  viewMode,
  filters,
  showMobileFilters,
  setShowMobileFilters,
  handleViewModeChange,
  setFilters,
  clearFilters,
  styles,
}) => (
  <div style={styles.mobileFilters}>
    <div
      style={styles.mobileFilterHeader}
      onClick={() => setShowMobileFilters(!showMobileFilters)}
    >
      <span>
        View by {viewMode === 'event' ? 'Event' : 'Track'}{' '}
        {(filters.track || filters.driver) && '• Filtered'}
      </span>
      <span>{showMobileFilters ? '▲' : '▼'}</span>
    </div>
    {showMobileFilters && (
      <div style={styles.mobileFilterContent}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button
            style={{
              ...styles.viewModeButton,
              ...(viewMode === 'event' ? styles.viewModeActive : {}),
              flex: 1,
            }}
            onClick={() => handleViewModeChange('event')}
          >
            Event
          </button>
          <button
            style={{
              ...styles.viewModeButton,
              ...(viewMode === 'track' ? styles.viewModeActive : {}),
              flex: 1,
            }}
            onClick={() => handleViewModeChange('track')}
          >
            Track
          </button>
        </div>
        <input
          key="mobile-track-filter"
          style={styles.filterInput}
          placeholder={viewMode === 'event' ? 'Track/Venue' : 'Track Name'}
          value={filters.track}
          onChange={(e) => setFilters({ ...filters, track: e.target.value })}
        />
        <input
          key="mobile-driver-filter"
          style={styles.filterInput}
          placeholder="Driver Name"
          value={filters.driver}
          onChange={(e) => setFilters({ ...filters, driver: e.target.value })}
        />
        <button style={styles.clearButton} onClick={clearFilters}>
          Clear Filters
        </button>
      </div>
    )}
  </div>
);

export default function EventListTable({
  liftSelectedSession,
  liftCreatingSession,
}) {
  const [viewMode, setViewMode] = useState('event'); // 'event' or 'track'
  const [loadingAllSessions, setLoadingAllSessions] = useState(false);
  const [filters, setFilters] = useState({
    track: '',
    driver: '',
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Data state
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState({}); // keyed by eventId
  const [videoData, setVideoData] = useState({}); // keyed by sessionId
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState({});
  const [loadingVideos, setLoadingVideos] = useState({});

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load events on component mount
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const result = await API.graphql({
        query: listEventLists,
        variables: {
          filter: {
            or: [
              { eventSource: { eq: 'migration' } },
              { eventSource: { eq: 'manual' } },
            ],
          },
        },
      });
      const eventItems = result.data.listEventLists.items || [];
      // Sort by most recent first
      const sortedEvents = eventItems.sort(
        (a, b) => new Date(b.eventStartDate) - new Date(a.eventStartDate)
      );
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadSessionsForEvent = async (eventId) => {
    setLoadingSessions((prev) => ({ ...prev, [eventId]: true }));
    try {
      const result = await API.graphql({
        query: listSessionLists,
        variables: {
          filter: {
            eventId: { eq: eventId },
          },
        },
      });
      const sessionItems = result.data.listSessionLists.items || [];
      setSessions((prev) => ({
        ...prev,
        [eventId]: sessionItems,
      }));

      // Load video data for all sessions to populate car cards
      sessionItems.forEach((session) => {
        loadVideoDataForSession(session.id);
      });
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions((prev) => ({ ...prev, [eventId]: false }));
    }
  };

  const loadVideoDataForSession = async (sessionId) => {
    setLoadingVideos((prev) => ({ ...prev, [sessionId]: true }));
    try {
      const result = await API.graphql({
        query: listVideoData,
        variables: {
          filter: {
            sessionId: { eq: sessionId },
          },
        },
      });
      const videoItems = result.data.listVideoData.items || [];
      setVideoData((prev) => ({
        ...prev,
        [sessionId]: videoItems,
      }));
    } catch (error) {
      console.error('Error loading video data:', error);
    } finally {
      setLoadingVideos((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  const loadAllSessions = async () => {
    setLoadingAllSessions(true);
    try {
      // Load sessions for all events that don't have them loaded yet
      const eventsNeedingSessions = events.filter(
        (event) => !sessions[event.id]
      );

      if (eventsNeedingSessions.length > 0) {
        const sessionPromises = eventsNeedingSessions.map(async (event) => {
          try {
            const result = await API.graphql({
              query: listSessionLists,
              variables: {
                filter: {
                  eventId: { eq: event.id },
                },
              },
            });
            const sessionItems = result.data.listSessionLists.items || [];

            // Load video data for these sessions too
            sessionItems.forEach((session) => {
              if (!videoData[session.id]) {
                loadVideoDataForSession(session.id);
              }
            });

            return { eventId: event.id, sessions: sessionItems };
          } catch (error) {
            console.error(
              `Error loading sessions for event ${event.id}:`,
              error
            );
            return { eventId: event.id, sessions: [] };
          }
        });

        const results = await Promise.all(sessionPromises);

        // Update sessions state with all loaded sessions
        const newSessions = { ...sessions };
        results.forEach(({ eventId, sessions: eventSessions }) => {
          newSessions[eventId] = eventSessions;
        });
        setSessions(newSessions);
      }
    } catch (error) {
      console.error('Error loading all sessions:', error);
    } finally {
      setLoadingAllSessions(false);
    }
  };

  const handleVideoSelect = (cardData) => {
    liftSelectedSession(cardData);
    liftCreatingSession(4); // Navigate to enhanced session viewer
  };

  const clearFilters = () => {
    setFilters({ track: '', driver: '' });
  };

  const handleViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);

    // When switching to track view, load all sessions to properly group them
    if (newViewMode === 'track') {
      loadAllSessions();
    }

    // When switching back to event mode, clear expanded groups to reset state
    if (newViewMode === 'event') {
      setExpandedGroups(new Set());
    }
  };

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
      // In event mode, load sessions when expanding an event
      // In track mode, groups are already loaded
      if (viewMode === 'event' && !sessions[groupId]) {
        loadSessionsForEvent(groupId);
      }
    }
    setExpandedGroups(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString();
  };

  // Group data by track when in track mode
  const getGroupedData = () => {
    if (viewMode === 'track') {
      const trackGroups = {};
      events.forEach((event) => {
        const trackKey = event.eventVenue || 'Unknown Track';
        if (!trackGroups[trackKey]) {
          trackGroups[trackKey] = {
            id: trackKey,
            name: trackKey,
            type: 'track',
            sessions: [],
            eventCount: 0,
          };
        }

        const eventSessions = sessions[event.id] || [];
        if (eventSessions.length > 0) {
          trackGroups[trackKey].eventCount++;
          eventSessions.forEach((session) => {
            trackGroups[trackKey].sessions.push({
              ...session,
              eventName: event.eventName,
              eventDate: event.eventStartDate,
              eventId: event.id,
              venue: event.eventVenue,
            });
          });
        }
      });

      // Sort sessions within each track by date (newest first)
      Object.values(trackGroups).forEach((track) => {
        track.sessions.sort(
          (a, b) => new Date(b.eventDate) - new Date(a.eventDate)
        );
      });

      // Filter out tracks with no sessions (if sessions haven't loaded yet)
      return Object.values(trackGroups).filter(
        (track) => track.sessions.length > 0
      );
    } else {
      // Return events as-is for event mode, but with sessions attached
      return events.map((event) => ({
        ...event,
        sessions: sessions[event.id] || [],
      }));
    }
  };

  const groupedData = getGroupedData();

  // Apply filters but don't exclude events/sessions without data
  const filteredData = groupedData
    .filter((group) => {
      // Filter by track/venue
      if (filters.track) {
        if (viewMode === 'event') {
          return group.eventVenue
            ?.toLowerCase()
            .includes(filters.track.toLowerCase());
        } else {
          // In track mode, filter by track name
          return group.name
            ?.toLowerCase()
            .includes(filters.track.toLowerCase());
        }
      }
      return true;
    })
    .map((group) => ({
      ...group,
      sessions: group.sessions.map((session) => ({
        ...session,
        cars: (videoData[session.id] || []).filter((video) => {
          // Only filter cars by driver name
          if (filters.driver) {
            return video.username
              ?.toLowerCase()
              .includes(filters.driver.toLowerCase());
          }
          return true;
        }),
      })),
    }));

  if (loadingEvents) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Events & Sessions</h1>
        <div style={styles.loading}>Loading events...</div>
      </div>
    );
  }

  if (loadingAllSessions) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Events & Sessions</h1>
        {!isMobile ? (
          <ViewModeSelector
            viewMode={viewMode}
            filters={filters}
            handleViewModeChange={handleViewModeChange}
            setFilters={setFilters}
            clearFilters={clearFilters}
            styles={styles}
          />
        ) : (
          <MobileViewModeSelector
            viewMode={viewMode}
            filters={filters}
            showMobileFilters={showMobileFilters}
            setShowMobileFilters={setShowMobileFilters}
            handleViewModeChange={handleViewModeChange}
            setFilters={setFilters}
            clearFilters={clearFilters}
            styles={styles}
          />
        )}
        <div style={styles.loading}>Loading all sessions for track view...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Events & Sessions</h1>

      {!isMobile ? (
        <ViewModeSelector
          viewMode={viewMode}
          filters={filters}
          handleViewModeChange={handleViewModeChange}
          setFilters={setFilters}
          clearFilters={clearFilters}
          styles={styles}
        />
      ) : (
        <MobileViewModeSelector
          viewMode={viewMode}
          filters={filters}
          showMobileFilters={showMobileFilters}
          setShowMobileFilters={setShowMobileFilters}
          handleViewModeChange={handleViewModeChange}
          setFilters={setFilters}
          clearFilters={clearFilters}
          styles={styles}
        />
      )}

      {filteredData.length === 0 ? (
        <div style={styles.emptyState}>
          {events.length === 0
            ? 'No manual events found. Create some events using the Session Upload Manager.'
            : 'No events match your current filters.'}
        </div>
      ) : (
        [...filteredData]
          .sort((a, b) => {
            const aDate = new Date(a.eventStartDate || a.eventDate || 0);
            const bDate = new Date(b.eventStartDate || b.eventDate || 0);
            return bDate - aDate;
          })
          .map((group) => (
            <div key={group.id} style={styles.eventCard}>
              <div
                style={styles.eventHeader}
                onClick={() => toggleGroup(group.id)}
              >
                <div>
                  <h3 style={styles.eventTitle}>
                    {group.eventName || group.name}
                  </h3>
                  <p style={styles.eventSubtitle}>
                    {viewMode === 'track'
                      ? `${group.sessions.length} sessions across ${group.eventCount} events`
                      : `${group.eventVenue} • ${formatDate(
                          group.eventStartDate
                        )}`}
                  </p>
                </div>
                <span>{expandedGroups.has(group.id) ? '▲' : '▼'}</span>
              </div>

              {expandedGroups.has(group.id) && (
                <>
                  {loadingSessions[group.id] ? (
                    <div style={styles.loading}>Loading sessions...</div>
                  ) : !group.sessions || group.sessions.length === 0 ? (
                    <div style={styles.emptyState}>
                      No sessions found for this event.
                    </div>
                  ) : (
                    [...group.sessions]
                      .sort((a, b) => {
                        const aDate = a.sessionStart
                          ? new Date(a.sessionStart)
                          : new Date(a.eventDate || group.eventStartDate);
                        const bDate = b.sessionStart
                          ? new Date(b.sessionStart)
                          : new Date(b.eventDate || group.eventStartDate);
                        return aDate - bDate;
                      })
                      .map((session, sessionIndex) => (
                        <div
                          key={session.id}
                          style={{
                            ...styles.sessionSection,
                            ...(sessionIndex === group.sessions.length - 1
                              ? { borderBottom: 'none' }
                              : {}),
                          }}
                        >
                          <div style={styles.sessionHeader}>
                            <h4 style={styles.sessionTitle}>
                              {session.sessionName}
                            </h4>
                            <span style={styles.sessionTime}>
                              {session.sessionStart
                                ? new Date(
                                    session.sessionStart
                                  ).toLocaleString()
                                : formatDate(
                                    session.eventDate || group.eventStartDate
                                  )}
                              {session.trackConfig &&
                                ` • ${session.trackConfig}`}
                            </span>
                          </div>

                          {loadingVideos[session.id] ? (
                            <div style={styles.loading}>Loading videos...</div>
                          ) : session.cars.length === 0 ? (
                            <div style={styles.emptyState}>
                              No video data found for this session.
                            </div>
                          ) : (
                            <div style={styles.carGrid}>
                              {session.cars.map((car) => {
                                let bestLap = undefined;
                                let totalLaps = undefined;
                                if (
                                  car.laptimeData &&
                                  Array.isArray(car.laptimeData) &&
                                  car.laptimeData.length > 0
                                ) {
                                  totalLaps = car.laptimeData.length;
                                  bestLap = Math.min(
                                    ...car.laptimeData
                                      .map((lap) => Number(lap.duration))
                                      .filter(Boolean)
                                  );
                                  if (!isFinite(bestLap)) bestLap = undefined;
                                }
                                return (
                                  <CarSessionCard
                                    key={car.id}
                                    car={car}
                                    session={session}
                                    group={group}
                                    isMobile={isMobile}
                                    viewMode={viewMode}
                                    bestLap={bestLap}
                                    totalLaps={totalLaps}
                                    onPlay={() =>
                                      handleVideoSelect({
                                        videoDataId: car.id,
                                        carNum: car.carNum,
                                        username: car.username,
                                        cameraAngle: car.cameraAngle,
                                        sessionId: session.id,
                                        sessionName: session.sessionName,
                                        trackConfig: session.trackConfig,
                                        eventId: group.id,
                                        eventName: group.eventName,
                                        eventVenue: group.eventVenue,
                                        eventDate: group.eventStartDate,
                                        hasVideo: !!(
                                          car.streamUrl ||
                                          car.youtubeStreamUrl ||
                                          car.tracksideVideoUrl ||
                                          car.tempLocalVideoLink
                                        ),
                                        hasTelemetry: !!(
                                          car.telemetryS3Key ||
                                          car.tracksideTelemetryUrl ||
                                          car.tempLocalTelemetryLink
                                        ),
                                        hasLaptimes: !!car.laptimeData,
                                      })
                                    }
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                  )}
                </>
              )}
            </div>
          ))
      )}
    </div>
  );
}
